import { useState, useRef } from 'react';
import { Plus, Trash2, Pencil, GripVertical, Shield, Sparkles } from 'lucide-react';
import { Tooltip } from '@mui/material';
import GenericTable from '../../../../components/table/GenericTable';
import { GENERIC_TABLE_DESIGN } from '../../../../components/table/GenericTable.design';
import { CHAMADOS_CONFIG_DESIGN } from './ChamadosConfig.design';
import ModalStatusChamado from './ModalStatusChamado';
import ModalConfimacao from 'src/components/modal/ModalConfimacao';
import {
  actionCriarStatusChamado,
  actionEditarStatusChamado,
  actionDeletarStatusChamado,
  actionReordenarStatusChamados,
} from 'src/actions/chamados/actionChamadosConfig';
import { Badge } from 'src/@/components/ui/badge';
import Iconify from 'src/components/Iconify';

const TABLE_HEAD = [
  { id: 'ordem', label: '', className: 'w-10' },
  { id: 'nome', label: 'Nome', className: 'flex-1' },
  { id: 'tipo', label: 'Tipo', className: 'w-32 text-center' },
  { id: 'actions', label: 'Ações', className: 'w-24 text-right' },
];

export default function StatusTab({ listaStatus, onRefresh, clienteId, caixaEntradaId }) {
  const [openModal, setOpenModal] = useState(false);
  const [openConfirmacao, setOpenConfirmacao] = useState(false);
  const [action, setAction] = useState('');
  const [loading, setLoading] = useState(false);
  const [statusId, setStatusId] = useState('');
  const [nome, setNome] = useState('');
  const [cor, setCor] = useState('#7C3AED');

  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  const totais = {
    total: listaStatus.length,
    ativos: listaStatus.filter(s => s.ativo).length,
    padrao: listaStatus.filter(s => s.tipo === 'padrao').length,
    custom: listaStatus.filter(s => s.tipo === 'custom').length,
  };

  function resetForm() {
    setNome('');
    setCor('#7C3AED');
    setStatusId('');
    setAction('');
  }

  async function handleCriar() {
    try {
      setLoading(true);
      await actionCriarStatusChamado({ nome, cor, cliente_id: clienteId, caixa_entrada_id: caixaEntradaId });
      await onRefresh();
      setOpenModal(false);
      resetForm();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleEditar() {
    try {
      setLoading(true);
      await actionEditarStatusChamado(statusId, { nome, cor });
      await onRefresh();
      setOpenModal(false);
      resetForm();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleDeletar() {
    try {
      setLoading(true);
      await actionDeletarStatusChamado(statusId);
      await onRefresh();
      setOpenConfirmacao(false);
      resetForm();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleDragEnd() {
    if (dragItem.current === null || dragOverItem.current === null) return;
    const reordered = [...listaStatus];
    const [removed] = reordered.splice(dragItem.current, 1);
    reordered.splice(dragOverItem.current, 0, removed);
    dragItem.current = null;
    dragOverItem.current = null;

    try {
      await actionReordenarStatusChamados({ ids: reordered.map(s => s._id) });
      await onRefresh();
    } catch (error) {
      console.log(error);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Action button */}
      <div className="flex justify-end">
        <button
          onClick={() => { setOpenModal(true); setAction('Criar'); }}
          className={CHAMADOS_CONFIG_DESIGN.page.actionButton}
        >
          <Plus size={18} />
          <span>Criar Status</span>
        </button>
      </div>

      {/* Modals */}
      <ModalConfimacao
        open={openConfirmacao}
        onClose={() => setOpenConfirmacao(false)}
        onConfirm={handleDeletar}
        loadingFinalizar={loading}
        mensagem="Deseja mesmo excluir esse status?"
      />
      <ModalStatusChamado
        open={openModal}
        action={action}
        nome={nome}
        changeNome={(e) => setNome(e.target.value)}
        cor={cor}
        changeCor={(value) => setCor(value)}
        onSubmit={action === 'Criar' ? handleCriar : handleEditar}
        onClose={() => { setOpenModal(false); resetForm(); }}
      />

      {/* Table */}
      {listaStatus.length === 0 ? (
        <div className={CHAMADOS_CONFIG_DESIGN.emptyState.container}>
          <div className={CHAMADOS_CONFIG_DESIGN.emptyState.iconBox}>
            <Shield size={32} />
          </div>
          <h3 className={CHAMADOS_CONFIG_DESIGN.emptyState.title}>Nenhum status encontrado</h3>
          <p className={CHAMADOS_CONFIG_DESIGN.emptyState.description}>
            Os status padrão serão criados automaticamente ao carregar a página.
          </p>
        </div>
      ) : (
        <GenericTable headLabels={TABLE_HEAD}>
          {listaStatus.map((row, index) => (
            <div
              key={row._id}
              className={`${GENERIC_TABLE_DESIGN.row.container} cursor-grab`}
              draggable
              onDragStart={() => { dragItem.current = index; }}
              onDragEnter={() => { dragOverItem.current = index; }}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => e.preventDefault()}
            >
              {/* Drag handle */}
              <div className="w-10 flex items-center text-gray-300">
                <GripVertical size={16} />
              </div>

              {/* Nome */}
              <div className="flex-1 min-w-[200px] pr-4">
                <div className="flex items-center gap-3">
                  <span
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: row.cor }}
                  />
                  <span className="text-sm font-semibold text-gray-900">{row.nome}</span>
                </div>
              </div>

              {/* Tipo */}
              <div className="w-32 text-center flex justify-center">
                {row.tipo === 'padrao' ? (
                  <Badge variant="outline" className="text-xs font-medium flex items-center gap-1.5 bg-blue-50 text-blue-700 border-blue-200">
                    <Shield size={10} />
                    Padrão
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs font-medium flex items-center gap-1.5 bg-violet-50 text-violet-700 border-violet-200">
                    <Sparkles size={10} />
                    Custom
                  </Badge>
                )}
              </div>

              {/* Actions */}
              <div className="w-24 flex justify-end gap-1">
                <Tooltip title="Editar">
                  <button
                    onClick={() => {
                      setOpenModal(true);
                      setAction('Editar');
                      setStatusId(row._id);
                      setNome(row.nome);
                      setCor(row.cor);
                    }}
                    className="p-2 rounded-lg text-gray-400 hover:bg-gray-50 hover:text-gray-600 transition-all"
                  >
                    <Pencil size={18} />
                  </button>
                </Tooltip>
                <Tooltip title={row.tipo === 'padrao' ? 'Status padrão não pode ser excluído' : 'Excluir'}>
                  <span>
                    <button
                      onClick={() => {
                        if (row.tipo === 'padrao') return;
                        setStatusId(row._id);
                        setOpenConfirmacao(true);
                      }}
                      disabled={row.tipo === 'padrao'}
                      className={`${GENERIC_TABLE_DESIGN.common.deleteButton} ${row.tipo === 'padrao' ? 'opacity-30 cursor-not-allowed' : ''}`}
                    >
                      <Trash2 size={18} />
                    </button>
                  </span>
                </Tooltip>
              </div>
            </div>
          ))}
        </GenericTable>
      )}
    </div>
  );
}
